/**
 * ── THE TEXTURE LOADER (plan §3 `texture`: png now, ktx2 at R2) ─────────────────────────────────
 *
 * Bytes → `THREE.Texture`, with the colour space decided by ROLE and nothing else. The two
 * measured facts behind that rule are Axon's (`axonRig.ts` MAPS): `rgb_mask` is sRGB and reads as
 * pale salmon when decoded linear; `base_color` is a LINEAR wear map and turns the white shell
 * mid-grey when decoded sRGB. A name is not a colour space; the role is. So: `albedo` → sRGB,
 * everything else (normal, orm, mask, a wear map, an unknown) → linear (`NoColorSpace`), and the
 * default is the SAFE direction — an sRGB decode of linear data is the loud failure, a linear read
 * of sRGB data the quiet one.
 *
 * WHERE THE ROLE COMES FROM. v1's catalog gives a `texture` asset its bytes under the file role
 * `image` (`KIND_REQUIRED_ROLES`), which says nothing about colour, and the catalog schema has no
 * first-class colour field until P4. The seam is `labels`, and the SPELLING is the cook's
 * (`tools/library/compose.ts`): an `asset.json`'s `image.use` / `image.colourspace` reach the
 * catalog as `map-<use>` and `colourspace-<srgb|linear>`. So:
 *   · `textureColorSpaceOf`: a `colourspace-*` label is AUTHORITATIVE (Axon's `rgb_mask` is a
 *     mask AND sRGB — the role rule alone would get it wrong); else the role rule;
 *   · `textureRoleOf`: `slot`, else a `map-<use>` label, else a bare known-role label, else `image`.
 *
 * Sampler state is PINNED (flipY false for glTF UVs, Repeat, trilinear, anisotropy 4) exactly as
 * `axonRig.ts` pins it: sampler state is in the WebGPU pipeline cache key (surfaceFactory rule 2),
 * and a default that drifts between two loaders forks a program.
 *
 * Decoding is INJECTABLE (`decodeImage`): the browser default is `createImageBitmap`, which Node
 * lacks — the service tests inject a decoder returning a sized image, and the Texture is built
 * the same way either side.
 */

import * as THREE from 'three/webgpu';

import {
  COLOURSPACE_LABEL_PREFIX,
  MAP_LABEL_PREFIX,
  TEXTURE_COLOR_ROLES,
  colorSpaceTokenForRole,
  textureColorSpaceTokenOf,
  textureRoleOf,
  type ColorSpaceToken,
  type TextureDecodeInput,
} from '../core/decode';

/*
 * ⭐ THE ROLE AND COLOUR-SPACE RULES MOVED TO `core/decode.ts` AND THIS FILE IS THEIR THREE-SIDED
 * SPELLING. They are read by the CATALOG SCHEMA now (a blob is one download and ONE decode, so a
 * catalog that reads one sha256 two ways is refused at publish rather than at acquire), and the
 * schema is pure — it may not see `THREE.SRGBColorSpace`. So the pure module decides WHICH colour
 * space as a token and this one maps the token onto three's constant. Both spellings, one rule.
 */
export {
  COLOURSPACE_LABEL_PREFIX,
  MAP_LABEL_PREFIX,
  TEXTURE_COLOR_ROLES,
  textureRoleOf,
  textureColorSpaceTokenOf,
  type ColorSpaceToken,
};

/** A token → three's constant. The ONE place the two vocabularies meet. */
export function colorSpaceOfToken(token: ColorSpaceToken): string {
  return token === 'srgb' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
}

/** An `ImageBitmap`-shaped image: what `THREE.Texture` uploads and `sizeOf` prices. */
export interface DecodedImage {
  width: number;
  height: number;
  close?(): void;
}

export type ImageDecoder = (bytes: ArrayBuffer, mime: string) => Promise<DecodedImage>;

export function colorSpaceForRole(role: string): string {
  return colorSpaceOfToken(colorSpaceTokenForRole(role));
}

/** A `colourspace-srgb` / `colourspace-linear` label is authoritative; otherwise the role decides. */
export function textureColorSpaceOf(asset: TextureDecodeInput): string {
  return colorSpaceOfToken(textureColorSpaceTokenOf(asset));
}

const MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** `png` → `image/png`; a format this loader cannot decode (`ktx2`, `basis`, …) → `null`. */
export function mimeForFormat(format: string): string | null {
  const base = format.split('+')[0] ?? format;
  return MIME[base] ?? null;
}

const defaultDecoder: ImageDecoder = async (bytes, mime) => {
  const g = globalThis as { createImageBitmap?: (blob: Blob) => Promise<DecodedImage> };
  if (typeof g.createImageBitmap !== 'function') {
    throw new Error('createImageBitmap is not available — inject `decodeImage`');
  }
  return g.createImageBitmap(new Blob([bytes], { type: mime }));
};

export interface DecodeTextureOptions {
  /** The catalog file's `format` (`png`, `jpg`, …). */
  format: string;
  /** The colour role (`textureRoleOf`). */
  role: string;
  /** An explicit colour space (`textureColorSpaceOf`); omitted ⇒ `colorSpaceForRole(role)`. */
  colorSpace?: string;
  decodeImage?: ImageDecoder;
}

/** Throws on an undecodable format or bytes — the service maps that to a failure state. */
export async function decodeTexture(bytes: ArrayBuffer, opts: DecodeTextureOptions): Promise<THREE.Texture> {
  const mime = mimeForFormat(opts.format);
  if (mime === null) throw new Error(`texture format '${opts.format}' has no decoder in this runtime`);
  const image = await (opts.decodeImage ?? defaultDecoder)(bytes, mime);
  // `Texture` accepts any upload-able image; a `DecodedImage` is the ImageBitmap shape.
  const tex = new THREE.Texture(image as unknown as ImageBitmap);
  tex.flipY = false;
  tex.colorSpace = opts.colorSpace ?? colorSpaceForRole(opts.role);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}
